import { MaccsBoxTicketType } from '../enums/MaccsBoxTicketType';
import { Media } from '../interfaces/Media';
import { SubscriptionBillingPeriodModel } from './subscription-billing-period.model';
import {
  BillingRenewalCycle,
  CardFormat,
  CardGenerationMode,
  EarliestStartDate,
  MinContractDurationPeriod,
  SubscriptionBillingPeriodType,
  SubscriptionCancellationPeriod,
  SubscriptionStartDateRequirement,
  SubscriptionType,
  SubscriptionValidityPeriod,
  UserDetailRequirement,
} from './subscription-enums';

export type SetupFeeRecognition = 'immediate' | 'over_minimum_contract';
/** PAUL-3670: how the journal books a subscription product's revenue. `per_admission` is for punch cards only. */
export type RevenueRecognition = 'immediate' | 'over_time' | 'per_admission';

export interface SubscriptionModel {
  id?: string;
  product_id: string;
  company_id: string;
  entity_status: number;
  type: SubscriptionType;

  // General info
  public_name: string;
  private_name: string;
  description?: string;
  group_ids?: string[];
  media_ids?: string[];
  media?: Media[];
  available_from?: string | null;
  available_to?: string | null;

  // POS
  shop_ids?: string[];
  pos_ids?: { pos_id: string; type: number[] }[];

  // Pricing (non-recurring only)
  price?: number;
  vat_id?: string;
  pricing_available_from?: string;
  original_price?: number;
  general_ledger_account_id?: string;

  // Billing (recurring only)
  billing_renewal_cycle?: BillingRenewalCycle;
  billing_date_for_periodic_renewal?: number;
  days_before_anniversary_renewal?: number;
  /** Derived from `billing_ledger_account_id` since PAUL-3045. Never sent. */
  billing_vat_id?: string;
  /** Legacy free text, still returned until the column is dropped (PAUL-3045). Shown nowhere, never sent. */
  billing_general_ledger_account?: string;
  /** PAUL-3670: the recurring fee's account, a Result account with a VAT; its VAT applies to every period. */
  billing_ledger_account_id?: string | null;
  billing_periods?: SubscriptionBillingPeriodModel[];
  sepa_direct_debit_enabled?: boolean | null;
  setup_fee_enabled?: boolean | null;
  setup_fee_amount?: number | null;
  /** Derived from `setup_fee_ledger_account_id` since PAUL-3045. Never sent. */
  setup_fee_vat_id?: string | null;
  setup_fee_ledger_account_id?: string | null;
  setup_fee_label?: string;
  /** PAUL-3670: Balance account overriding the company's default deferred revenue account; null = the default. */
  deferred_revenue_ledger_account_id?: string | null;
  /** PAUL-3670: when the setup fee becomes revenue; only shown while the setup fee is on. */
  setup_fee_recognition?: SetupFeeRecognition;
  /** PAUL-3670: every type; recurring defaults to `over_time`, passes to `immediate`. */
  revenue_recognition?: RevenueRecognition;

  // Unique codes
  card_format?: CardFormat;
  card_length?: number;
  card_prefix?: string;
  card_generation_mode?: CardGenerationMode;

  // Contract terms (recurring only)
  earliest_start_date?: EarliestStartDate;
  days_in_future_for_start?: number;
  min_contract_duration_applies: boolean;
  min_contract_duration_period?: MinContractDurationPeriod;
  min_contract_duration_quantity?: number;
  cancellation_period?: SubscriptionCancellationPeriod;
  cancellation_period_quantity?: number;
  block_on_no_shows: boolean;
  max_no_shows_before_blocking?: number;
  block_on_non_payment: boolean;
  days_before_blocking?: number;
  switch_billing_cycle_after_min_commitment: boolean;
  default_billing_period_after_switch?: SubscriptionBillingPeriodType;

  // Validity (non-recurring only)
  limit_availability: boolean;
  validity_period?: SubscriptionValidityPeriod;
  validity_period_quantity?: number;
  period_start?: string;
  period_end?: string;

  // Linked products
  ticket_product_ids: string[];

  // Discounts
  discount_to_ids: string[];
  discount_with_ids: string[];

  // Rules
  max_amount_of_tickets_enabled: boolean;
  max_amount_of_tickets?: number;
  limit_per_show: boolean;
  amount_per_show?: number;
  min_duration_between_shows_enabled: boolean;
  min_duration_between_shows_minutes?: number;
  max_active_reservations_enabled: boolean;
  max_active_reservations?: number;
  scanning_card_required: boolean;
  // Admits a card holder without a booking: the door issues the free ticket on the spot.
  auto_issue_at_door: boolean;
  // PAUL-3745: the pass also covers a film format's surcharge (3D, IMAX). Off: holders pay it on top.
  include_movie_surcharge?: boolean;
  requires_account: boolean;
  allow_cancelling_reservations: boolean;
  // Lifetime sell cap — a sold slot is only freed by a refund or a released reservation.
  max_quantity_enabled?: boolean;
  max_quantity?: number | null;
  // Asked of the buyer at checkout, not of the operator: it dates the subscription, and for a
  // punchcard or passepartout the validity period is counted from it.
  start_date_requirement?: SubscriptionStartDateRequirement;

  // User details
  is_personal: boolean;
  email_requirement: UserDetailRequirement;
  phone_requirement: UserDetailRequirement;
  first_name_requirement: UserDetailRequirement;
  last_name_requirement: UserDetailRequirement;
  birthday_requirement: UserDetailRequirement;
  gender_requirement: UserDetailRequirement;
  image_requirement: UserDetailRequirement;
  address_requirement: UserDetailRequirement;
  zipcode_requirement: UserDetailRequirement;
  city_requirement: UserDetailRequirement;
  country_requirement: UserDetailRequirement;

  // Box office
  include_only_scanned: boolean;
  box_office_rules?: SubscriptionBoxOfficeRuleModel[];
}

export interface SubscriptionBoxOfficeRuleModel {
  id?: string;
  subscription_id?: string;
  available_from?: string;
  amount?: number;
  /**
   * PAUL-3449: Maccsbox TicketTypeCd; only Normal/Discount/Other are accepted. Optional only for reading:
   * rules saved before the column existed come back without it and the form falls back to Normal. Always
   * send it when saving - the backend stores Normal for an omitted value, also on edit.
   */
  ticket_type?: MaccsBoxTicketType;
}

export interface SubscriptionListModel {
  product_id: string;
  public_name: string;
  private_name: string;
  subscription_type: SubscriptionType;
  entity_status: number;
  available_from?: string | null;
  available_to?: string | null;
}

export interface SubscriptionCardBatchModel {
  id: string;
  company_id: string;
  inserted?: string;
  mode: number;
  amount?: number;
  prefix?: string;
  imported: number;
  // Codes of this batch already consumed.
  used_count?: number;

  // Import outcome (PAUL-2019). A row that does not match the card shape, or repeats an existing
  // code, is a counted skip and the rest of the file still imports, so a successful upload can have
  // imported fewer codes than the file held. Drive the summary off the counts: `details` is capped,
  // so its length is not the number of skipped rows.
  total?: number;
  skipped_duplicate?: number;
  skipped_invalid?: number;
  details?: CodeImportDetail[];
  /** True when more rows were skipped than `details` could carry. */
  details_truncated?: boolean;

  /** @deprecated Unlocalized free text, superseded by `details`. Server drops it after 2026-11-12. */
  errors?: string[];
  /** @deprecated False whenever any row was skipped, which says nothing about whether the import ran. */
  success?: boolean;
}

// Mirrors GiftCardCodeImportKind: the same server contract (ICodeImportSummary) drives both.
export enum CodeImportKind {
  Duplicate = 'duplicate',
  InvalidFormat = 'invalid_format',
}

/** One row the import could not take, and why. */
export interface CodeImportDetail {
  line: number;
  code?: string;
  kind: CodeImportKind | string;
}
